import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/16/solid';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Button, Select } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { ChatEnum } from '../../../../Chat.enum.ts';
import { useChatContext } from '../../../../Chat.hook.ts';
import { useMessageBarContext } from '../../MessageBar.hook.ts';
import QuickActionRow from './components/QuickActionRow/QuickActionRow.component.tsx';
import './QuickAction.styles.scss';
import { tQuickActionProps } from './QuickAction.types.ts';
import {
  getQuickReplySearch,
  getQuickReplyTriggerIndex,
  insertQuickReplyText,
} from './QuickAction.functions.ts';
import { getModuleName } from '../../../../Chat.utils.ts';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';

export default function QuickAction(props: tQuickActionProps) {
  const { quickReplies, replyMessage, textAreaRef } = useChatContext();
  const {
    setChatAttachments,
    passDataToLegacyInput,
    setSelectedAction,
    setMessageBarValue,
    quickReplyTriggerRef,
  } = useMessageBarContext();
  const { t } = useTranslation('chat/chat');
  const { trackEvent } = useAnalytics();

  const [selectValue, setSelectValue] = useState('0');
  const [inputValue, setInputValue] = useState('');
  const [inputSearchValue, setInputSearchValue] = useState('');
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const quickActionClassName = classNames('QuickAction', {
    'QuickAction--replyMessageSelected': replyMessage,
  });

  let filteredQuickReplies = quickReplies || [];

  if (inputValue || inputSearchValue) {
    filteredQuickReplies = filteredQuickReplies.filter((quick) =>
      quick.shortCommand
        .toLowerCase()
        .includes(
          inputValue.toLowerCase() || inputSearchValue.toLocaleLowerCase(),
        ),
    );
  }

  if (selectValue !== '0') {
    filteredQuickReplies = filteredQuickReplies.filter((quick) =>
      selectValue === '1' ? quick.isPublic : !quick.isPublic,
    );
  }

  useEffect(() => {
    const handleTextAreaChange = (e: Event) => {
      // BUG-1157: con el panel abierto, el keydown de abajo hace
      // stopPropagation y TextAreaContainer no ve la nueva `/`; se guarda aquí.
      if ((e as InputEvent).data === '/' && textAreaRef.current) {
        quickReplyTriggerRef.current = textAreaRef.current.selectionStart - 1;
      }
      const triggerOptions = {
        text: textAreaRef.current?.value || '',
        cursor: textAreaRef.current?.selectionStart ?? 0,
        triggerIndex: quickReplyTriggerRef.current,
      };

      if (getQuickReplyTriggerIndex(triggerOptions) === -1) {
        setSelectedAction(ChatEnum.CHAT);
        return;
      }
      setInputSearchValue(getQuickReplySearch(triggerOptions));
    };

    const textAreaElement = textAreaRef.current;
    if (textAreaElement) {
      textAreaElement.addEventListener('input', handleTextAreaChange);
    }

    return () => {
      if (textAreaElement) {
        textAreaElement.removeEventListener('input', handleTextAreaChange);
      }
    };
  }, [textAreaRef]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      e.stopPropagation();

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          Math.min(prev + 1, filteredQuickReplies.length - 1),
        );
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      }

      if (e.key === 'Enter' && filteredQuickReplies.length > 0) {
        e.preventDefault();
        const firstQuickReplay =
          filteredQuickReplies[selectedIndex] || filteredQuickReplies[0];
        if (!firstQuickReplay) return;

        const attachments =
          firstQuickReplay.content.attachments?.map((file) => ({
            type: file.mimeType || '',
            src: file.url || '',
            name: file.filename || '',
            size: 0,
            poster: '',
          })) || [];

        const target = e.target as HTMLTextAreaElement;
        const newValue = insertQuickReplyText({
          text: target.value,
          cursor: target.selectionStart,
          triggerIndex: quickReplyTriggerRef.current,
          replyText: firstQuickReplay.content.text?.trim() || '',
        });

        setMessageBarValue(newValue);
        setChatAttachments(attachments);
        setSelectedAction(ChatEnum.CHAT);
      }
    };
    const textAreaElement = textAreaRef.current;
    if (textAreaElement) {
      textAreaElement.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      if (textAreaElement) {
        textAreaElement.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [filteredQuickReplies, textAreaRef, selectedIndex]);

  useEffect(() => {
    if (itemsRef.current[selectedIndex]) {
      itemsRef.current[selectedIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (selectedIndex >= filteredQuickReplies.length) {
      setSelectedIndex(filteredQuickReplies.length - 1);
    }
  }, [filteredQuickReplies]);

  useEffect(() => {
    setSelectedIndex((prev) => Math.min(prev, filteredQuickReplies.length - 1));
  }, [filteredQuickReplies.length]);

  return (
    <div className={quickActionClassName}>
      <div className="QuickAction__Header">
        <p>{t('quickActions.title')}</p>
        <XMarkIcon
          onClick={() => setSelectedAction(ChatEnum.CHAT)}
          className="QuickAction__Header__Icon"
        />
      </div>
      <div className="QuickAction__Filters">
        <div className="QuickAction__Filters__InputContainer">
          <MagnifyingGlassIcon className="QuickAction__Filters__InputContainer__Icon" />
          <p>/</p>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setSelectedIndex(0);
            }}
            onBlur={() => {
              setTimeout(() => textAreaRef.current?.focus(), 100);
            }}
            onFocus={() => setSelectedIndex(0)}
            className="QuickAction__Filters__InputContainer__Input"
            placeholder={t('quickActions.searchInput')}
          />
        </div>

        <Select
          className="QuickAction__Filters__Select"
          value={selectValue}
          onChange={(e) => {
            setSelectValue(e.target.value);
            setSelectedIndex(0);
            textAreaRef.current?.focus();
          }}
        >
          <option value="0">{t('quickActions.availableOption01')}</option>
          <option value="1">{t('quickActions.availableOption02')}</option>
          <option value="2">{t('quickActions.availableOption03')}</option>
        </Select>
        <Button
          onClick={() => {
            passDataToLegacyInput(ChatEnum.CREATE_QUICK_ACTION);
            trackEvent('front.chat.quick_replies.create_btn.click', {
              displayName: 'Crear respuesta rápida',
              description:
                'Cuántos tenian la intención de crear una respuesta rápida',
              ctaType: getModuleName(),
            });
          }}
          color="primary"
          className="col-span-3 w-full"
        >
          + {t('quickActions.newQuickAction')}
        </Button>
      </div>
      <div
        className="flex flex-col overflow-auto"
        onFocus={() => textAreaRef.current?.focus()}
      >
        {filteredQuickReplies?.map((quickActionRow, index) => {
          return (
            <QuickActionRow
              key={index}
              ref={(el) => {
                if (el) itemsRef.current[index] = el;
              }}
              index={index}
              selectedIndex={selectedIndex || 0}
              quickActionState={props.quickActionState}
              setQuickActionState={props.setQuickActionState}
              {...quickActionRow}
            />
          );
        })}
      </div>
    </div>
  );
}
